// pages/Admin/RoleAdminPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, TextField, Button, MenuItem, Select, FormControl, InputLabel,
  Chip, Tooltip, IconButton, Switch, FormControlLabel, Typography,
  Checkbox, ListItemText, InputAdornment, Avatar
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Badge, CheckCircle, Cancel, Add, Edit, Search } from '@mui/icons-material';
import MainText from '../../components/MainText';
import { RBAC } from '../../services/api';
import { useAuth } from '../../auth/AuthProvider';
import { addTab } from '../../tabs/tabsSlice';
import { useDispatch } from 'react-redux';
import CustomModal from '../../components/CustomModal';
import Autocomplete from '@mui/material/Autocomplete';
import { 
  Briefcase as BriefcaseIcon, // Ícono principal para roles (ej: Manager)
  UserCheck as ActiveIcon,      // Ícono para estado Activo (Check)
  UserX as InactiveIcon,        // Ícono para estado Inactivo (X)
} from 'lucide-react';
import CustomSnackBar from '../../components/CustomSnackBar'

const uniqById = (arr) => Array.from(new Map((arr || []).map(o => [o.id, o])).values());
const companyInitial = (name) =>
  (name || '').trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase() || 'C';

// NUEVO: sugerencias de rol (puedes editar esta lista)
const roleSuggestions = [
  'Finanzas', 'Compras', 'Ventas', 'Inventarios',
  'Gerencia', 'Contabilidad', 'Bodega', 'Tesorería',
  'Auditoría', 'Administrador', 'Soporte', 'Supervisor',
];

/* ---------- Tags de permisos (con +N en popover simple) ---------- */
function PermTagsCell({ perms = [] }) {
  const list = Array.isArray(perms) ? perms : [];
  if (!list.length) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const max = 3;
  const visible = list.slice(0, max);
  const rest = list.length - visible.length;

  return (
    <Stack direction="row" spacing={0.75} sx={{ overflow: 'hidden', flexWrap: 'wrap' }}>
      {visible.map(p => (
        <Chip
          key={p.id || p.code}
          size="small"
          variant="outlined"
          label={p.code || String(p)}
          sx={{ maxWidth: 160 }}
        />
      ))}
      {rest > 0 && (
        <Chip size="small" variant="filled" color="default" label={`+${rest}`} />
      )}
    </Stack>
  );
}

/* ---------- Form Crear Rol (compacto) ---------- */
// function CreateRoleForm({ setOpen, companies, cat, defaultCompanyId, onCreated }) {
//   const [cid, setCid] = useState(defaultCompanyId || (companies[0]?.id || ''));
//   const [active, setActive] = useState(true);
//   const [permIds, setPermIds] = useState([]);
//   const [name, setName] = useState('');

//   const permOptions = useMemo(() => uniqById(cat), [cat]);

//   const submit = async () => {
//     if (!cid || !name.trim()) return;
//     try {
//       await RBAC.createRole({
//         name: name.trim(),
//         is_active: active,
//         company: cid,
//         permission_ids: permIds,
//       });
//       setOpen(false);
//       onCreated?.();
//     } catch { /* opcional snackbar */ }
//   };

//   return (
//     <Stack spacing={2}>
//       <FormControl size="small" fullWidth>
//         <InputLabel>Empresa</InputLabel>
//         <Select label="Empresa" value={cid} onChange={(e) => setCid(e.target.value)}>
//           {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
//         </Select>
//       </FormControl>

//       {/* Nombre del rol (texto libre; sin “negrita intensa” en tabla) */}
//       <Autocomplete
//         freeSolo
//         options={roleSuggestions}
//         value={name}
//         onInputChange={(_, v) => setName(v || '')}
//         onChange={(_, v) => setName(typeof v === 'string' ? v : (v || ''))}
//         renderInput={(params) => (
//           <TextField
//             {...params}
//             size="small"
//             label="Nombre del rol"
//             placeholder="Selecciona o ingresa el nombre del rol…"
//           />
//         )}
//       />

//       {/* Selector múltiple compacto de permisos */}
//       <Autocomplete
//         multiple
//         disableCloseOnSelect
//         options={permOptions}
//         getOptionLabel={(o) => o.code || ''}
//         renderOption={(props, option, { selected }) => (
//           <li {...props} key={option.id}>
//             <Checkbox checked={selected} sx={{ mr: 1 }} />
//             <ListItemText primary={option.code} secondary={option.description} />
//           </li>
//         )}
//         value={permOptions.filter(p => permIds.includes(p.id))}
//         onChange={(_, vals) => setPermIds(vals.map(v => v.id))}
//         renderInput={(params) => (
//           <TextField {...params} size="small" label="Permisos" placeholder="Selecciona permisos" />
//         )}
//         renderTags={(value) => <Chip size="small" label={`${value.length} permisos`} />}
//       />

//       <FormControlLabel
//         control={<Switch checked={active} onChange={(_, v) => setActive(v)} />}
//         label={active ? 'Activo' : 'Inactivo'}
//       />

//       <Stack direction="row" justifyContent="flex-end" spacing={1}>
//         <Button onClick={() => setOpen(false)}>Cancelar</Button>
//         <Button variant="contained" onClick={submit}>Crear</Button>
//       </Stack>
//     </Stack>
//   );
// }

 function CreateRoleForm({
  setOpen,
  companies = [],
  cat = [],
  defaultCompanyId,
  onCreated,
}) {
  const [cid, setCid] = useState(defaultCompanyId || companies[0]?.id || "");
  const [active, setActive] = useState(true);
  const [permIds, setPermIds] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  // snackbar
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info"); // 'success' | 'error' | 'warning' | 'info'

  const permOptions = useMemo(() => uniqById(cat), [cat]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!cid || !trimmed) {
      setSnackMsg(!trimmed ? "Ingresa un nombre de rol." : "Selecciona una empresa.");
      setSnackSev("warning");
      setSnackOpen(true);
      return;
    }
    setLoading(true);
    try {
      await RBAC.createRole({
        name: trimmed,
        is_active: active,
        company: cid,
        permission_ids: permIds,
      });

      setSnackMsg("Rol creado correctamente.");
      setSnackSev("success");
      setSnackOpen(true);

      // reset + callback
      setName("");
      setPermIds([]);
      onCreated?.();
      // cierra el modal un pelito después para que el usuario vea el toast
      setTimeout(() => setOpen(false), 350);
    } catch (e) {
      setSnackMsg("No se pudo crear el rol. Revisa que no este repetido.");
      setSnackSev("error");
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Stack spacing={2}>
        <FormControl size="small" fullWidth>
          <InputLabel>Empresa</InputLabel>
          <Select
            label="Empresa"
            value={cid}
            onChange={(e) => setCid(e.target.value)}
          >
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Nombre del rol */}
        <Autocomplete
          freeSolo
          options={roleSuggestions}
          value={name}
          onInputChange={(_, v) => setName(v || "")}
          onChange={(_, v) => setName(typeof v === "string" ? v : v || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Nombre del rol"
              placeholder="Selecciona o ingresa el nombre del rol…"
            />
          )}
        />

        {/* Permisos */}
        <Autocomplete
          multiple
          disableCloseOnSelect
          options={permOptions}
          getOptionLabel={(o) => o?.code || ""}
          value={permOptions.filter((p) => permIds.includes(p.id))}
          onChange={(_, vals) => setPermIds(vals.map((v) => v.id))}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.id}>
              <Checkbox checked={selected} sx={{ mr: 1 }} />
              <ListItemText
                primary={option.code}
                secondary={option.description}
              />
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Permisos"
              placeholder="Selecciona permisos"
            />
          )}
          renderTags={(value) => (
            <Chip size="small" label={`${value.length} permisos`} />
          )}
        />

        <FormControlLabel
          control={
            <Switch
              checked={active}
              onChange={(_, v) => setActive(v)}
            />
          }
          label={active ? "Activo" : "Inactivo"}
        />

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={() => setOpen(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={submit}
            disabled={loading}
          >
            {loading ? "Creando…" : "Crear"}
          </Button>
        </Stack>
      </Stack>

      {/* Snackbar */}
      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </>
  );
}/* ---------- Form Editar Rol (compacto) ---------- */
function EditRoleForm({ setOpen, role, cat, onUpdated }) {
  const [active, setActive] = useState(!!role?.is_active);
  const [permIds, setPermIds] = useState((role?.permissions || []).map(p => p.id));
  const [name, setName] = useState(role?.name || '');

  const permOptions = useMemo(() => uniqById(cat), [cat]);

  const submit = async () => {
    if (!role?.id || !name.trim()) return;
    try {
      await RBAC.updateRole(role.id, {
        name: name.trim(),
        is_active: active,
        permission_ids: permIds,
      });
      setOpen(false);
      onUpdated?.();
    } catch { /* opcional snackbar */ }
  };

  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        label="Nombre del rol"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={permOptions}
        getOptionLabel={(o) => o.code || ''}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.id}>
            <Checkbox checked={selected} sx={{ mr: 1 }} />
            <ListItemText primary={option.code} secondary={option.description} />
          </li>
        )}
        value={permOptions.filter(p => permIds.includes(p.id))}
        onChange={(_, vals) => setPermIds(vals.map(v => v.id))}
        renderInput={(params) => (
          <TextField {...params} size="small" label="Permisos" placeholder="Selecciona permisos" />
        )}
        renderTags={(value) => <Chip size="small" label={`${value.length} permisos`} />}
      />
      <FormControlLabel
        control={<Switch checked={active} onChange={(_, v) => setActive(v)} />}
        label={active ? 'Activo' : 'Inactivo'}
      />
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={() => setOpen(false)}>Cancelar</Button>
        <Button variant="contained" onClick={submit}>Guardar</Button>
      </Stack>
    </Stack>
  );
}

/* ======================= Página ======================= */
export default function RoleAdminPage() {
  const { permissions, isSuperuser } = useAuth();
  const dispatch = useDispatch();

  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [statusActive, setStatusActive] = useState(true);
  const [q, setQ] = useState('');
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cat, setCat] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);

  const canManage = isSuperuser || (Array.isArray(permissions) && permissions.includes('manage_roles'));

  useEffect(() => { dispatch(addTab({ label: 'Roles', path: '/admin/roles' })); }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        if (isSuperuser) {
          const { data } = await RBAC.companies.list();
          setCompanies(data || []);
        } else {
          const { data } = await RBAC.userCompanies('me');
          setCompanies((data || []).map(uc => uc.company).filter(Boolean));
        }
      } catch { setCompanies([]); }
    })();
  }, [isSuperuser]);

  useEffect(() => {
    (async () => { try { const { data } = await RBAC.permissions(); setCat(data || []); } catch { } })();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        company: companyId || undefined,
        active: statusActive ? 'true' : 'false',
        search: q || undefined,
      };
      const { data } = await RBAC.rolesList(params);
      setRoles(Array.isArray(data) ? data : []);
    } catch { setRoles([]); }
    finally { setLoading(false); }
  }, [companyId, statusActive, q]);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (row) => {
    try { await RBAC.updateRole(row.id, { is_active: !row.is_active }); load(); } catch { }
  };

  /* --------- Columnas --------- */
  const columns = useMemo(() => ([
    { field: 'id', headerName: 'ID', width: 70 },
{
  field: 'name', 
  headerName: 'Rol', 
  flex: 1, 
  minWidth: 180,
  renderCell: (p) => {
    // Definimos el estado
    const isActive = p.row?.is_active;
    
    // Definimos los elementos visuales basados en el estado
    const StatusIcon = isActive ? ActiveIcon : InactiveIcon;
    const statusColor = isActive ? 'success.main' : 'warning.main'; 
    const statusText = isActive ? 'Activo' : 'Inactivo';
    
    return (
      <Stack 
        direction="row" 
        spacing={1.5} 
        alignItems="center"
        // Añadimos un hover sutil para darle interacción
        sx={{ 
          height: '100%', 
          cursor: 'pointer',
          p: 1,
          borderRadius: 1,
          '&:hover': {
            backgroundColor: 'action.hover'
          }
        }}
      >
        {/* 1. Icono principal del rol (visual de alto nivel) */}
        <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
          <BriefcaseIcon size={20} />
        </Box>
        
        {/* 2. Stack de Texto (Nombre del Rol + Subtexto/Estatus) */}
        <Box>
          <Typography 
            variant="body2" 
            sx={{ fontWeight: 700, lineHeight: 1.15 }}
          >
            {p.value} {/* Nombre del Rol */}
          </Typography>
          
          {/* 3. Subtexto de estatus con ícono miniatura */}
          <Tooltip title={`Estatus: ${statusText}`} arrow>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StatusIcon size={12} color={statusColor} /> 
              <Typography 
                variant="caption" 
                sx={{ 
                  color: statusColor, 
                  fontWeight: 600, 
                  lineHeight: 1.15 
                }}
              >
                {statusText}
              </Typography>
            </Stack>
          </Tooltip>
        </Box>
      </Stack>
    );
  }
},
    {
      field: 'company', headerName: 'Empresa', width: 220,
      renderCell: (p) => {
        const c = p?.row?.company;
        const name = typeof c === 'object' ? c?.name : c;
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Avatar
              sx={{ width: 24, height: 24, fontSize: 12 }}
            >
              {companyInitial(name)}
            </Avatar>
            <Typography variant="body2">{name || '—'}</Typography>
          </Stack>
        );
      }
    },
    {
      field: 'permissions', headerName: 'Permisos', flex: 1.4, minWidth: 280, sortable: false,
      renderCell: (p) => <PermTagsCell perms={p?.row?.permissions} />
    },
    {
      field: 'is_active', headerName: 'Estado', width: 110,
      renderCell: (p) => {
        const active = !!p?.row?.is_active;
        return <Chip size="small" label={active ? 'Activo' : 'Inactivo'} color={active ? 'success' : 'default'} variant={active ? 'filled' : 'outlined'} />;
      }
    },
    {
      field: 'actions', headerName: 'Acciones', width: 110, sortable: false,
      renderCell: (p) => {
        const row = p?.row;
        if (!row || !canManage) return null;
        const active = !!row?.is_active;
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={active ? 'Desactivar' : 'Activar'}>
              <IconButton size="small" onClick={() => toggleActive(row)}>
                {active ? <Cancel fontSize="small" color="warning" /> : <CheckCircle fontSize="small" color="success" />}
              </IconButton>
            </Tooltip>
            {canManage && (
              <Tooltip title="Editar">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); setCurrentRole(p.row); setEditOpen(true); }}
                >
                  <Edit fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [canManage]);

  /* --------- Estilos del grid (como ListOrder) --------- */
  const gridSx = {
    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#fff' },
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover': {
      backgroundColor: '#10173c !important',
    },
    '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator':
      { color: '#ffffff !important' },
    '& .MuiDataGrid-cell': {
      borderBottom: '1px solid #f0f0f0',
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(16,23,60,.08)', cursor: 'pointer' },
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Encabezado + botón crear a la derecha */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2, gap: 2 }}
      >
        <MainText title={'Roles'} subtitle={'Administración de roles'} icon={Badge} width="auto" />
        {canManage && (
          <Button
            variant="contained"
            onClick={() => setCreateOpen(true)}
            startIcon={<Add />}
            sx={{ ml: 'auto', borderRadius: 2, px: 1.5, py: 0.75, textTransform: 'none', fontWeight: 700 }}
          >
            Crear rol
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          {companyId && (
            <Chip label={`Empresa: ${companies.find(c => c.id === companyId)?.name || companyId}`} onDelete={() => setCompanyId('')} />
          )}
          {q && <Chip label={`Búsqueda: "${q}"`} onDelete={() => setQ('')} />}
          {!companyId && !q && statusActive && <Chip label="Mostrando activos" color="info" />}
          {!statusActive && <Chip label="Inactivos" color="error" onDelete={() => setStatusActive(true)} />}
        </Box>
      </Stack>

      {/* Filtros */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 1 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Empresa</InputLabel>
          <Select value={companyId} label="Empresa" onChange={(e) => setCompanyId(e.target.value)}>
            <MenuItem value="">(Todas)</MenuItem>
            {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Buscar rol"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            )
          }}
        />

        <FormControlLabel
          control={<Switch checked={statusActive} onChange={(_, v) => setStatusActive(v)} />}
          label={statusActive ? 'Activos' : 'Inactivos'}
        />
      </Stack>

      {/* Chips indicadores */}


      {/* Grid a pantalla completa (menos encabezado/filtros) */}
      <Box sx={{ height: 'calc(100vh - 230px)', width: '100%' }}>
        <DataGrid
          rows={roles}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowHeight={45}
          columnHeaderHeight={50}
          sx={gridSx}
        />
      </Box>

      {/* Modales */}
      <CustomModal
        open={createOpen}
        setOpen={setCreateOpen}
        title="Crear nuevo rol"
        component={CreateRoleForm}
        componentProps={{
          companies,
          cat,
          defaultCompanyId: companyId,
          onCreated: load
        }}
        width="520px"
      />

      <CustomModal
        open={editOpen}
        setOpen={setEditOpen}
        title={`Editar rol`}
        component={EditRoleForm}
        componentProps={{
          role: currentRole,
          cat,
          onUpdated: load
        }}
        width="520px"
      />
    </Box>
  );
}
